import React from 'react'
import { format, parseISO, differenceInDays } from 'date-fns'
import { AlertTriangle, FileX, ChevronDown, Eye } from 'lucide-react'
import { RevFinOpsPayable } from '@/lib/revfinops-api'
import { PayableStatusBadge } from './PayableStatusBadge'

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface PayablesTableProps {
  payables: RevFinOpsPayable[]
  loading: boolean
  error: string | null
  onRetry: () => void
  onRowClick?: (payableId: string) => void
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Format currency value to BRL
 */
function formatCurrency(amount: number, currency: string = 'BRL'): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2
  }).format(amount)
}

/**
 * Format date to DD/MM/YYYY
 */
function formatDate(dateString: string): string {
  try {
    return format(parseISO(dateString), 'dd/MM/yyyy')
  } catch {
    return dateString
  }
}

/**
 * Get CSS class for due date based on urgency
 * - Overdue: red/error
 * - Due soon (≤3 days): orange/warning
 * - Normal: gray
 */
function getDueDateClass(dueDateString: string): string {
  try {
    const daysUntilDue = differenceInDays(parseISO(dueDateString), new Date())

    if (daysUntilDue < 0) return 'text-error font-semibold' // Overdue
    if (daysUntilDue <= 3) return 'text-warning font-semibold' // Due soon
    return 'text-brand-gray-light' // Normal
  } catch {
    return 'text-brand-gray-light'
  }
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

/**
 * LoadingSkeleton - Animated skeleton for loading state
 */
function LoadingSkeleton() {
  return (
    <div className="table-container">
      <table className="table">
        <thead className="table-header">
          <tr>
            <th>Payable ID</th>
            <th>Amount</th>
            <th>
              <div className="flex items-center gap-1">
                Due Date
                <ChevronDown size={14} className="text-brand-primary" />
              </div>
            </th>
            <th>Status</th>
            <th>Assigned To</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody className="table-body">
          {[...Array(8)].map((_, i) => (
            <tr key={i}>
              <td>
                <div className="loading-pulse h-4 w-20 rounded bg-gray-700" />
              </td>
              <td>
                <div className="loading-pulse h-4 w-24 rounded bg-gray-700" />
              </td>
              <td>
                <div className="loading-pulse h-4 w-24 rounded bg-gray-700" />
              </td>
              <td>
                <div className="loading-pulse h-6 w-20 rounded-full bg-gray-700" />
              </td>
              <td>
                <div className="loading-pulse h-4 w-28 rounded bg-gray-700" />
              </td>
              <td>
                <div className="loading-pulse h-4 w-16 rounded bg-gray-700" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * EmptyState - Shown when no payables exist
 */
function EmptyState() {
  return (
    <div className="card flex flex-col items-center justify-center min-h-[400px] space-y-4">
      <FileX className="h-16 w-16 text-brand-gray opacity-50" />
      <h3 className="text-xl font-display font-semibold text-white">
        No Payables in Queue
      </h3>
      <p className="text-brand-gray-light text-center max-w-md font-body">
        There are currently no payables to process. New payables will appear here when transactions are created.
      </p>
    </div>
  )
}

/**
 * ErrorState - Shown when API call fails
 */
function ErrorState({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div className="card flex flex-col items-center justify-center min-h-[400px] space-y-4">
      <AlertTriangle className="h-16 w-16 text-error" />
      <h3 className="text-xl font-display font-semibold text-white">
        Error Loading Payables
      </h3>
      <p className="text-brand-gray-light text-center max-w-md font-body">
        {error || 'An unexpected error occurred while loading payables.'}
      </p>
      <button onClick={onRetry} className="btn-primary">
        Retry
      </button>
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * PayablesTable - Displays RevFinOps payables in a table format
 *
 * Features:
 * - Loading skeleton state
 * - Empty state when no payables
 * - Error state with retry
 * - Sorted by due date (soonest first)
 * - Color-coded status badges
 * - Highlighted overdue/due-soon dates
 * - Formatted currency and dates
 * - Clickable rows for navigation
 * - Keyboard navigation support
 * - Accessibility labels
 *
 * @param payables - Array of payable objects (sorted)
 * @param loading - Loading state flag
 * @param error - Error message if any
 * @param onRetry - Callback to retry loading
 * @param onRowClick - Optional callback when row is clicked
 */
export function PayablesTable({
  payables,
  loading,
  error,
  onRetry,
  onRowClick,
}: PayablesTableProps) {
  // Handle row click navigation
  const handleRowClick = (payableId: string) => {
    if (onRowClick) {
      onRowClick(payableId)
    }
  }

  // Handle keyboard navigation (Enter/Space)
  const handleKeyDown = (e: React.KeyboardEvent, payableId: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleRowClick(payableId)
    }
  }

  // Loading state
  if (loading) {
    return <LoadingSkeleton />
  }

  // Error state
  if (error) {
    return <ErrorState error={error} onRetry={onRetry} />
  }

  // Empty state
  if (!payables || payables.length === 0) {
    return <EmptyState />
  }

  // Render table with data
  return (
    <div className="table-container">
      <table className="table">
        <thead className="table-header">
          <tr>
            <th>Payable ID</th>
            <th>Amount</th>
            <th>
              <div className="flex items-center gap-1">
                Due Date
                <ChevronDown size={14} className="text-brand-primary" />
              </div>
            </th>
            <th>Status</th>
            <th>Assigned To</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody className="table-body">
          {payables.map((payable) => (
            <tr
              key={payable.id}
              onClick={() => handleRowClick(payable.id)}
              onKeyDown={(e) => handleKeyDown(e, payable.id)}
              tabIndex={0}
              className="table-row-clickable"
              role="button"
              aria-label={`View payable ${payable.id.slice(0, 8)}`}
            >
              <td className="font-mono text-xs text-brand-gray-light">
                {payable.id.slice(0, 8)}...
              </td>
              <td className="font-semibold text-white">
                {formatCurrency(payable.amount, payable.currency)}
              </td>
              <td className={getDueDateClass(payable.dueDate)}>
                {formatDate(payable.dueDate)}
              </td>
              <td>
                <PayableStatusBadge status={payable.status} />
              </td>
              <td className="text-brand-gray-light">
                {payable.clientName || '-'}
              </td>
              <td>
                <div className="flex items-center gap-2">
                  <button
                    className="btn-ghost p-2"
                    aria-label={`View payable details`}
                    title="View details"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <span className="table-cell-action">
                    View
                  </span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default PayablesTable
